import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuditEntrySchema } from './audit.js';
import { frozenRateFields, rateOverrideFields, rateOverrideInputFields, rateOverridePatchFields, documentCurrencyInputFields, documentCurrencyPatchFields } from './fx.js';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { BillStatsSchema } from './document-stats.js';
import { DocumentDateRangeQuery, PickerIdsSchema, pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
const c = initContract();
/**
 * Purchase Invoices / supplier bills (PART 7, PUR-03 on SAL-00).
 *
 * BR-DATA-09 — "one thing must have exactly one name on screen across the
 * whole product. A reader who meets two names believes there are two things."
 * §17.4.6 names this record as one of its two offenders (system map "Purchase
 * Bill", flow drawing "Purchase Invoice (Bill)", data model "PurchaseInvoice")
 * and settles it: **Purchase Bill on screen, `PurchaseInvoice` in the
 * database**. This file is the seam where the two meet, and it holds the line
 * in both directions — the table, the route prefix and the types stay
 * `purchase_invoices` / `PurchaseInvoice`, while every string a user reads
 * says Bill (`BILL_MESSAGES`, the `bill.*` i18n keys, `bill_no` printed as
 * BILL-####). The rule's other offender is settled the same way: Tasks on
 * screen, `Task` in the database. Anything new that needs two names picks one
 * for each side and writes the pairing down where the seam is, as here.
 *
 * Permission area: `purchasing` (16.2.2 row 2), per action:
 *   list/get = purchasing.view · create = purchasing.create · edit draft =
 *   purchasing.edit · delete draft = purchasing.delete · post AND accept a
 *   variance = purchasing.approve (BR-PUR-03-03: "a bill must not be posted
 *   without the approve permission"; BR-PUR-03-16: a manager holding it may
 *   accept a variance — logged with a reason). Per 16.4.2 the AP Clerk and
 *   the Accountant enter bills (RCU) but neither releases one — that is the
 *   "the person who enters a supplier bill is not the person who releases
 *   it" separation the grid is built for.
 *
 * The three-way match (§7.5) is computed, never stored (BR-PUR-03-14):
 *   matched  — the bill equals its order, to the cent (BR-PUR-03-12's strict
 *              equality; the tolerance band is an open question left at its
 *              spec default until org settings exist).
 *   variance — they differ. Blocks POSTING (and posting is what makes a bill
 *              payable — BR-PUR-03-01 to the extent computable now) until a
 *              purchasing.approve holder accepts it with a reason.
 *   no_order — a service bill with no order behind it (BR-PUR-03-15's
 *              carve-out): legal, flagged, no match to run.
 *   The received leg is LIVE: once a posted GRN exists against the order,
 *   receivedTotal carries Σ accepted value at the order's prices and the
 *   match demands the bill's taxable base equal it (§7.5's partial-billing
 *   answer: the bill is compared against what has been RECEIVED so far).
 *
 * Lifecycle per §7.7/R8, exactly the sales invoice's: draft freely editable
 * and deletable; POST writes the PUR-03 §12 entry through the ledger in ONE
 * transaction (DR the lines' expense accounts / DR 2120 VAT / CR 2111 Trade
 * Payables — the chart's postable codes, not the prose's 2110 header);
 * posted answers 409 to any edit but the notes. Cancel/debit note = a later
 * slice (PUR-04).
 */
// ── The fixed sentences of PUR-03 ───────────────────────────────────────────
export const BILL_MESSAGES = {
    /**
     * G-10 (duplicate invoice detection) — the two WARNINGS, never refusals.
     * They ride the `warnings` array on a write response; the exact-match
     * duplicate reference is still REFUSED by `duplicateSupplierRef` below, so
     * the reference warning fires only on a near miss (spacing/punctuation).
     */
    duplicateRefWarning: 'Another bill from this supplier carries the same supplier reference. Check it is not the same invoice.',
    duplicateTotalWarning: 'Another bill from this supplier has the same total within a week of this date. Check it is not the same invoice.',
    /** ERR-PUR-03-02 / BR-PUR-03-01, quoted exactly. */
    varianceBlocked: 'This bill does not match what was ordered and received. A manager must accept the difference.',
    /** BR-PUR-03-05 / ERR-PUR-03-04, the spec's own shape. */
    duplicateSupplierRef: (billCode, ref, supplier) => `Bill ${billCode} already records ${ref} from ${supplier}.`,
    /** BR-PUR-03-05's required-at-posting half. */
    supplierRefRequired: "Enter the supplier's own invoice number before posting.",
    /** BR-PUR-03-06. */
    dueBeforeDate: 'The due date must not be earlier than the bill date.',
    /** BR-PUR-03-07 / ERR-PUR-03-05. */
    currencyMismatch: (orderCode, currency) => `Order ${orderCode} is in ${currency}. The bill must be in ${currency}.`,
    /** R8 via §7.7 — the posted lock, debit-note flavoured (BR-PUR-03-11). */
    postedLocked: 'This document is posted. Correct it with a debit note.',
    postedNoDelete: 'A posted bill cannot be deleted. Correct it with a debit note.',
    notADraft: 'Only a draft can be posted.',
    /** BR-SAL-00-15/-16 shapes. */
    noLines: 'Add at least one line before posting.',
    zeroTotal: 'A document for 0.00 cannot be posted.',
    /** A line's chosen account is an expense leaf; goods reach 1131 via the GRN. */
    expenseAccountKind: 'A bill line can only debit an expense account.',
    /**
     * Direct-invoice-to-stock (inventory.bill_writes_stock): a product line has
     * to say which warehouse the goods arrive into before the bill can post.
     */
    warehouseRequired: (description) => `Pick the warehouse ${description} arrives into.`,
    /** BR-PUR-01-08 + billing an uncommitted order. */
    orderNotBillable: (code) => `${code} has not been sent to the supplier and cannot be billed yet.`,
    orderClosed: (code) => `${code} is closed. It cannot be received or billed again.`,
    /** Accepting a variance needs one. */
    reasonRequired: 'A variance can only be accepted with a reason, which is written to the audit log.',
    /** Only a real variance can be accepted. */
    nothingToAccept: 'This bill matches its order. There is no variance to accept.',
    /** BR-PUR-03-03 / ERR-PUR-03-03 — the amber banner. */
    approvalBanner: "You don't have permission to approve this bill for payment — sent to an approver.",
    /** BR-PUR-03-11's tail / ERR-PUR-03-06, the spec's own sentence with the live amount. */
    paymentMatched: (amount) => `A payment of ${amount} is matched to this bill. Unmatch it first.`,
    /** GEN-74, the spec's own shape: name the blocker. */
    debitNoteStands: (returnCode) => `Cancel ${returnCode} first — it was made from this bill.`,
    /** Only the posted state cancels (GEN-70; a draft deletes instead). */
    cancelOnlyPosted: 'Only a posted bill can be cancelled. A draft is edited or deleted instead.',
    /** GEN-72: the cancelling act carries its reason. */
    cancelReasonRequired: 'A posted bill can only be cancelled with a reason, which is written to the audit log.',
    /**
     * Wave 1C — the no-GRN branch of the match counts bills already POSTED on
     * the order (two bills each equal to the order used to both read
     * "matched"); an order whose posted bills already cover it cannot take
     * another.
     */
    orderFullyBilled: (code) => `${code} is already fully billed. Nothing is left to bill against it.`,
    /**
     * Wave 1C — stock items on a sent order with NOTHING received: posting the
     * bill would expense goods that never arrived. A purchasing.approve holder
     * may still post with `allowUnreceived` set, on purpose.
     */
    nothingReceived: (code) => `Nothing has been received against ${code}. Post a goods receipt first, or confirm that this bill is to be posted before the goods arrive.`,
    /**
     * Wave 1C — the four-eyes rule on a variance above the company's
     * tolerance: the approver who accepted it may not also be the one who
     * posts (Q-FIN-04-2's shape on the bill side).
     */
    varianceSelfApproval: 'The variance on this bill was accepted by you. A different approver must post it.',
};
/**
 * D46 — PUR-03's CONVERTED parameterised refusals, structured: GEN-74's two
 * cancel blockers on the purchase side, the exact mirror of
 * `SALES_REFUSALS`. Sentence parity with the `BILL_MESSAGES` twins is pinned
 * by `refusal-census.spec.ts`.
 */
export const BILL_REFUSALS = {
    paymentMatched: (amount) => ({
        message: BILL_MESSAGES.paymentMatched(amount),
        key: 'bills.errPaymentMatched',
        params: { amount },
    }),
    debitNoteStands: (returnCode) => ({
        message: BILL_MESSAGES.debitNoteStands(returnCode),
        key: 'bills.errDebitNoteStands',
        params: { code: returnCode },
    }),
    // ── d46tail: PUR-03's three remaining parameterised refusals ──────────────
    /** BR-PUR-03-05 — the supplier's own invoice number is already recorded. All
     *  display-value params: a bill code, the ref, the supplier name. */
    duplicateSupplierRef: (billCode, ref, supplier) => ({
        message: BILL_MESSAGES.duplicateSupplierRef(billCode, ref, supplier),
        key: 'bills.errDuplicateSupplierRef',
        params: { billCode, ref, supplier },
    }),
    /** BR-PUR-01-08 — the order was never sent to the supplier. */
    orderNotBillable: (code) => ({
        message: BILL_MESSAGES.orderNotBillable(code),
        key: 'bills.errOrderNotBillable',
        params: { code },
    }),
    /** The order is closed — byte-identical to GRN's orderClosed; each family
     *  carries its own key. */
    orderClosed: (code) => ({
        message: BILL_MESSAGES.orderClosed(code),
        key: 'bills.errOrderClosed',
        params: { code },
    }),
    /** Direct-invoice-to-stock: a product line needs a warehouse to post into.
     *  The inbound twin of DELIVERY_REFUSALS.warehouseRequired — its own key,
     *  because goods here ARRIVE rather than leave. */
    warehouseRequired: (description) => ({
        message: BILL_MESSAGES.warehouseRequired(description),
        key: 'bills.errWarehouseRequired',
        params: { description },
    }),
    // ── wave 1C: the match's two order-naming refusals ────────────────────────
    /** The order's posted bills already cover it; `code` is the order's code. */
    orderFullyBilled: (code) => ({
        message: BILL_MESSAGES.orderFullyBilled(code),
        key: 'bills.errOrderFullyBilled',
        params: { code },
    }),
    /** Stock items on a sent order, nothing received; `code` is the order's code. */
    nothingReceived: (code) => ({
        message: BILL_MESSAGES.nothingReceived(code),
        key: 'bills.errNothingReceived',
        params: { code },
    }),
    /** BR-PUR-03-07 — a bill from an order is in the order's currency. */
    currencyMismatch: (orderCode, currency) => ({
        message: BILL_MESSAGES.currencyMismatch(orderCode, currency),
        key: 'bills.errCurrencyMismatch',
        params: { code: orderCode, currency },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const BillStatusSchema = z.enum(['draft', 'posted', 'cancelled']);
export const MatchStatusSchema = z.enum(['matched', 'variance', 'no_order']);
export const BillLineInputSchema = z
    .object({
    itemId: z.uuid().optional(),
    description: z.string().trim().min(1).max(200),
    qty: QtyStringSchema,
    unitPrice: MoneyStringSchema,
    discountPct: PctStringSchema.optional(),
    /** NULL = the 5010 Purchases default. Expense accounts only (server-checked). */
    expenseAccountId: z.uuid().optional(),
    /**
     * Direct-invoice-to-stock (inventory.bill_writes_stock). Required at posting
     * for a product line when a STANDALONE bill writes stock — where the goods
     * land. Ignored on the order/GRN-backed path. Mirrors the sales-invoice line.
     */
    warehouseId: z.uuid().optional(),
    /** The unit the qty was typed in; NULL = the item's stock unit. */
    unitId: z.uuid().optional(),
    /** Tracking capture for a serial/batch item on the direct-to-stock path. */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
})
    .strict();
export const BillTwoWayAmountSchema = z.union([
    z.object({ pct: PctStringSchema }).strict(),
    z.object({ amount: MoneyStringSchema }).strict(),
]);
export const BillLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    itemId: z.uuid().nullable(),
    itemCode: z.string().nullable(),
    description: z.string(),
    qty: z.string(),
    unitPrice: z.string(),
    discountPct: z.string(),
    lineTotal: z.string(),
    expenseAccountId: z.uuid().nullable(),
    /** The resolved posting account (the default when expenseAccountId is null). */
    expenseAccountCode: z.string(),
    /** Direct-invoice-to-stock fields; NULL on the order/GRN-backed path. */
    warehouseId: z.uuid().nullable(),
    warehouseName: z.string().nullable(),
    unitId: z.uuid().nullable(),
    baseQty: z.string().nullable(),
    /** Wave 4C: the unit `qty` is in — the typed unit, else the item's stock unit; both null on a non-item line. */
    unitSymbol: z.string().nullable(),
    unitPrecision: UnitPrecisionSchema.nullable(),
    batchNo: z.string().nullable(),
    serialNos: z.string().array().nullable(),
})
    .strict();
/**
 * Bill header parity with the sales invoice (owner decision 2026-10-30 —
 * "the same box on new sales invoice should be on purchase invoice").
 * The sales header's cells, buyer-side spelling:
 *
 *   projectId          the project this purchase belongs to.
 *   partnerBranchId    the SUPPLIER's branch, a record of the partner's
 *                      (`partnersContract.listBranches`); must be one of
 *                      the bill's supplier's own (400 otherwise). The
 *                      server writes its NAME into `partnerBranch`.
 *   partnerBranch      the branch as text (kept beside the id, as on sales).
 *   manualRef, bookNo  the hand-written reference and paper booklet number.
 *   purchaserUserId    the user who made the purchase; defaults to whoever
 *                      creates the bill.
 *
 * CREATE (optional), PATCH (null clears), RESPONSE (nullable, names resolved
 * server-side so a reader without the project/user list permission sees them).
 */
export const BillHeaderInputFields = {
    projectId: z.uuid().optional(),
    partnerBranchId: z.uuid().optional(),
    partnerBranch: z.string().trim().max(120).optional(),
    manualRef: z.string().trim().max(60).optional(),
    bookNo: z.string().trim().max(40).optional(),
    purchaserUserId: z.uuid().optional(),
};
export const BillHeaderPatchFields = {
    projectId: z.uuid().nullable().optional(),
    partnerBranchId: z.uuid().nullable().optional(),
    partnerBranch: z.string().trim().max(120).nullable().optional(),
    manualRef: z.string().trim().max(60).nullable().optional(),
    bookNo: z.string().trim().max(40).nullable().optional(),
    purchaserUserId: z.uuid().nullable().optional(),
};
export const BillHeaderResponseFields = {
    projectId: z.uuid().nullable(),
    projectName: z.string().nullable(),
    partnerBranchId: z.uuid().nullable(),
    /** The branch record's CURRENT name (null when none is picked or it is gone). */
    partnerBranchName: z.string().nullable(),
    partnerBranch: z.string().nullable(),
    manualRef: z.string().nullable(),
    bookNo: z.string().nullable(),
    purchaserUserId: z.uuid().nullable(),
    purchaserName: z.string().nullable(),
};
export const BillSchema = z
    .object({
    id: z.uuid(),
    /** BILL-#### — its own series. */
    code: z.string(),
    billNo: z.number().int().positive(),
    status: BillStatusSchema,
    invoiceDate: IsoDateSchema,
    dueDate: IsoDateSchema,
    partnerId: z.uuid(),
    partnerCode: z.string(),
    partnerName: z.string(),
    /** The provenance pill: the order this bill came from (null = service bill). */
    purchaseOrderId: z.uuid().nullable(),
    purchaseOrderCode: z.string().nullable(),
    /** BR-PUR-03-05. */
    supplierInvoiceNo: z.string().nullable(),
    /** Header parity with the sales invoice (owner decision 2026-10-30). */
    ...BillHeaderResponseFields,
    currency: z.string(),
    /** EC-19-10: the rate this document froze when it was confirmed, and its "as of" date. */
    ...frozenRateFields,
    /** Wave 4D — the typed-rate flag and the market hint (fx.ts). */
    ...rateOverrideFields,
    lines: BillLineSchema.array(),
    subtotal: z.string(),
    discountPct: z.string(),
    discountAmount: z.string(),
    taxPct: z.string(),
    taxAmount: z.string(),
    grandTotal: z.string(),
    /**
     * The §7.5 match panel, computed on every read (BR-PUR-03-14):
     * ordered/received/billed, verdict, and the override state.
     * receivedTotal = Σ ACCEPTED value over POSTED goods receipts against
     * the order, at the order's prices; null exactly while no receipt has
     * posted (the strip's dash — and the match falls back to the two-figure
     * comparison). With receipts, matched additionally demands the bill's
     * taxable base equal the received value (§7.5's third figure live).
     */
    matchStatus: MatchStatusSchema,
    orderedTotal: z.string().nullable(),
    /**
     * Wave 1C — the order's grand total LESS the bills already POSTED on it
     * (for a posted bill: those posted before it) — the figure the no-receipt
     * branch of the match compares this bill against, carried so the screen's
     * between-keystrokes mirror (`matchVerdict`) reads the same basis the
     * server does. Null with no order.
     */
    orderedOpen: z.string().nullable(),
    receivedTotal: z.string().nullable(),
    varianceAccepted: z.boolean(),
    varianceReason: z.string().nullable(),
    /** Wave 1C — who accepted the variance (the four-eyes rule at posting). */
    varianceAcceptedBy: z.uuid().nullable(),
    /**
     * Wave 1C — true when any line names a stock-tracked item (a product or
     * a bundle). A no-order bill with none is a SERVICE bill; with one it is
     * goods arriving without a receipt, and the screen must not call it a
     * service bill.
     */
    hasStockLines: z.boolean(),
    /**
     * Wave 1C — calendar days from the company's today (N-16) to the due
     * date, computed on the server: positive = due in N days, zero = due
     * today, negative = N days overdue.
     */
    daysToDue: z.number().int(),
    /** F-8/F-9 mirrored: allocations from POSTED payments only (R9). */
    amountPaid: z.string(),
    balanceDue: z.string(),
    fullyPaid: z.boolean(),
    /** BR-MD-09-09, the payable mirror — see sales-invoices.ts for the rule. */
    pastDue: z.enum(['amber', 'red']).nullable(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    postedAt: z.string().nullable(),
    /** GEN-72's stamp trio — set once by cancel, null otherwise. */
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    cancellationJournalEntryId: z.uuid().nullable(),
    createdAt: z.string(),
    /**
     * G-10 — non-blocking warnings from a WRITE (create/update/post): the
     * `BILL_MESSAGES.*Warning` sentences. Absent on reads and lists.
     */
    warnings: z.string().array().optional(),
})
    .strict();
export const CreateBillRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverrideInputFields,
    /** Per-document currency (fx.ts documentCurrencyInputFields). */
    ...documentCurrencyInputFields,
    /**
     * "Create bill" from a PO carries the order across (PUR-01 §5c): when
     * given and `lines` is omitted, the server copies the order's supplier,
     * currency, lines and totals onto the new draft.
     */
    purchaseOrderId: z.uuid().optional(),
    partnerId: z.uuid().optional(),
    invoiceDate: IsoDateSchema,
    /** Defaults from the supplier's terms; no terms = due the bill date. */
    dueDate: IsoDateSchema.optional(),
    supplierInvoiceNo: z.string().trim().min(1).max(60).optional(),
    ...BillHeaderInputFields,
    lines: BillLineInputSchema.array().min(1).optional(),
    discount: BillTwoWayAmountSchema.optional(),
    tax: BillTwoWayAmountSchema.optional(),
    notes: z.string().max(2000).optional(),
})
    .strict();
export const UpdateBillRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverridePatchFields,
    /** Per-document currency, draft only (fx.ts documentCurrencyPatchFields). */
    ...documentCurrencyPatchFields,
    invoiceDate: IsoDateSchema.optional(),
    dueDate: IsoDateSchema.optional(),
    supplierInvoiceNo: z.string().trim().min(1).max(60).nullable().optional(),
    ...BillHeaderPatchFields,
    lines: BillLineInputSchema.array().min(1).optional(),
    discount: BillTwoWayAmountSchema.optional(),
    tax: BillTwoWayAmountSchema.optional(),
    /** The ONE field editable after posting (BR-SAL-00-22 via §7.7). */
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
export const AcceptVarianceRequestSchema = z
    .object({
    /** BR-PUR-03-02/-16: the override is logged with who and WHY. */
    reason: z.string().trim().min(1, BILL_MESSAGES.reasonRequired).max(500),
})
    .strict();
/**
 * Wave 1C — posting a bill for stock items on a sent order while NOTHING has
 * been received is refused (`nothingReceived`) unless the approver says so
 * explicitly. The flag is the explicit say-so; the permission is the route's
 * own (purchasing.approve).
 */
export const PostBillRequestSchema = z
    .object({
    allowUnreceived: z.boolean().optional(),
})
    .strict();
export const CancelBillRequestSchema = z
    .object({
    /** GEN-72: cancelling must ask for a reason and store it. */
    reason: z.string().trim().min(1, BILL_MESSAGES.cancelReasonRequired).max(500),
})
    .strict();
/**
 * PUR-03 §5e — the history card "filters the shared audit log to bills", and
 * SAL-04 §5e (the layout PUR-01 §5f says it shares) sets the depth: "up to
 * SIX entries, newest first". The six-cap is the SERVER's promise — the card
 * draws what the wire hands it, so an uncapped or misordered response would
 * quietly break the spec's card while still looking like a history.
 */
export const BILL_HISTORY_LIMIT = 6;
export const BillHistorySchema = z
    .object({ entries: AuditEntrySchema.array().max(BILL_HISTORY_LIMIT) })
    .strict();
const billParams = z.object({ billId: z.uuid() }).strict();
export const purchaseInvoicesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/purchases/bills',
        query: pagedQuery({
            partnerId: z.uuid().optional(),
            status: BillStatusSchema.optional(),
            purchaseOrderId: z.uuid().optional(),
            /** GEN-50: the printed bill number, or the supplier's name. */
            q: z.string().optional(),
            /** GEN-50: the bill a debit note is already holding. */
            ids: PickerIdsSchema.optional(),
            /** Wave 3B — bounds on the bill's invoice date, both inclusive. */
            ...DocumentDateRangeQuery,
        }),
        responses: {
            200: pagedResponse('bills', BillSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Supplier bills (purchasing.view). Only POSTED bills reach payables/aging/statements (R9, BR-PUR-03-10) — drafts appear here and nowhere else.',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail: the bills on either side of
     * this one in number order (null at an end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/purchases/bills/:billId/neighbours',
        pathParams: billParams,
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next bill by number (purchasing.view).',
    },
    /** S4 — the "Go to no." box: one bill by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/purchases/bills/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One bill by its printed number (purchasing.view).',
    },
    get: {
        method: 'GET',
        path: '/api/purchases/bills/:billId',
        pathParams: billParams,
        responses: {
            200: BillSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One bill with its lines and computed match panel (purchasing.view).',
    },
    history: {
        method: 'GET',
        path: '/api/purchases/bills/:billId/history',
        pathParams: billParams,
        responses: {
            200: BillHistorySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: "PUR-03 §5e: the bill screen's history card — the shared audit log filtered to THIS bill, up to six entries, newest first (BR-PUR-03-09). Gated by purchasing.view, the same door as the bill it describes — DSH-09's admin.read_audit_log does not bind here.",
    },
    create: {
        method: 'POST',
        path: '/api/purchases/bills',
        body: CreateBillRequestSchema,
        responses: {
            201: BillSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft bill (purchasing.create). With purchaseOrderId and no lines, the order is carried across (supplier, currency, lines, totals). The order must be sent+ and not closed/cancelled (BR-PUR-01-08).',
    },
    update: {
        method: 'PATCH',
        path: '/api/purchases/bills/:billId',
        pathParams: billParams,
        body: UpdateBillRequestSchema,
        responses: {
            200: BillSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (purchasing.edit); a posted bill answers 409 except a notes-only change (R8 via §7.7).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/purchases/bills/:billId',
        pathParams: billParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (purchasing.delete) — R8; posted answers 409 (BR-PUR-03-11).',
    },
    acceptVariance: {
        method: 'POST',
        path: '/api/purchases/bills/:billId/accept-variance',
        pathParams: billParams,
        body: AcceptVarianceRequestSchema,
        responses: {
            200: BillSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Accept a variance (purchasing.approve, BR-PUR-03-16) with a reason written to the audit log (BR-PUR-03-02). Unblocks posting.',
    },
    post: {
        method: 'POST',
        path: '/api/purchases/bills/:billId/post',
        pathParams: billParams,
        body: PostBillRequestSchema.optional(),
        responses: {
            200: BillSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post bill (purchasing.approve, BR-PUR-03-03) — Draft → Posted: refuses an unaccepted variance (BR-PUR-03-01) and a missing/duplicate supplier invoice number (BR-PUR-03-05), then writes DR expense accounts / DR 2120 VAT / CR 2111 Trade Payables through the ledger in ONE transaction.',
    },
    cancel: {
        method: 'POST',
        path: '/api/purchases/bills/:billId/cancel',
        pathParams: billParams,
        body: CancelBillRequestSchema,
        responses: {
            200: BillSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Cancel a POSTED bill (purchasing.approve — BR-PUR-03-11): reverses the posting entry on the bill's own date (GEN-72) and releases the GRN lines' invoiced_qty. Refused while a posted payment is matched ('unmatch it first'), while a posted debit note stands (GEN-74), and into a closed period (GEN-73). Reason required and audited (bill.cancel).",
    },
    /** Wave 3B — the list's tiles over the WHOLE register (see `document-stats.ts`). */
    stats: {
        method: 'GET',
        path: '/api/purchases/bills/stats',
        responses: {
            200: BillStatsSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'PUR-03 list tiles (purchasing.view): payable outstanding, overdue count and value, due within 7 days, bills with a match variance — posted-only where it is money, base currency, in SQL.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=purchase-invoices.js.map